import FullScreenLoader from "../../../BON Frontend/Booking-Form/Common/FullScreenLoader";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import "../../../reservation pages/Property Details/Rate-Inventory-management/Rate.css";
import Hearder from "../../commun/Hearder";
import SideBar from "../../commun/SideBar";
import {
    PropertySaveMapping,
    PropertyRateCode,
    PropertyMealplan,
    roomlistdetail,
} from "../../../Services/Service";

const rateFields = [
    { key: "base", label: "Base Rate", apiField: "BaseRate" },
    { key: "double", label: "Double Rate", apiField: "DoubleRate" },
    { key: "triple", label: "Triple Rate", apiField: "TripleRate" },
    { key: "extraBed", label: "Extra Bed", apiField: "ExtraBed" },
    { key: "extraAdult", label: "Extra Adult", apiField: "ExtraAdultRate" },
    { key: "extraChild", label: "Extra Child", apiField: "ExtraChildRate" },
];

const emptyRateSet = () => ({
    base: "",
    double: "",
    triple: "",
    extraBed: "",
    extraAdult: "",
    extraChild: "",
});

function Icon({ name, className = "", ...rest }) {
    return (
        <span className={`material-symbols-outlined ${className}`.trim()} {...rest}>
            {name}
        </span>
    );
}

export default function PropertyAddRateInventory() {
    const navigate = useNavigate();
    const location = useLocation();
    const editData = location.state?.editData || null; // row object when opened from Edit, else null (Add mode)

    const [loading, setLoading] = useState(false);

    const [ratecode, setRatecode] = useState([]);
    const [mealplan, setMealplan] = useState([]);
    const [roomCategories, setRoomCategories] = useState([]); // real rooms, built from ResRoomlist

    const [selectedRate, setSelectedRate] = useState("");
    const [selectedMealPlan, setSelectedMealPlan] = useState("");
    const [effectiveFrom, setEffectiveFrom] = useState("");
    const [effectiveTo, setEffectiveTo] = useState("");
    const [isActive, setIsActive] = useState(true);

    // Tracks RateCodeMealPlanID per room so we know create (0) vs update for each room
    const [roomEditIds, setRoomEditIds] = useState({});

    const [rates, setRates] = useState({});

    const [copyAllPopup, setCopyAllPopup] = useState({
        open: false,
        roomId: null,
    });
    const [adjustmentType, setAdjustmentType] = useState("Add Fixed Amount ($)");
    const [adjustmentValue, setAdjustmentValue] = useState("");
    const [selectedApplyFields, setSelectedApplyFields] = useState([]);

    const popupRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setCopyAllPopup({ open: false, roomId: null });
            }
        }
        if (copyAllPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [copyAllPopup.open]);

    // ── Fetch Rate Codes, Meal Plans, Rooms ─────────────────
    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const rateResult = await PropertyRateCode();
                if (Array.isArray(rateResult)) setRatecode(rateResult);

                const mealResult = await PropertyMealplan();
                if (Array.isArray(mealResult)) setMealplan(mealResult);

                const roomResult = await roomlistdetail();
                const roomsList = Array.isArray(roomResult) ? roomResult : [];

                const categories = roomsList.map((room) => ({
                    id: room.RoomID,
                    title: room.RoomName,
                }));
                setRoomCategories(categories);

                const initialRates = {};
                categories.forEach((room) => {
                    initialRates[room.id] = emptyRateSet();
                });
                setRates(initialRates);
            } catch (error) {
                console.log("error fetching form data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // ── Pre-fill form when opened in Edit mode (after rooms are loaded) ──
    useEffect(() => {
        if (editData && roomCategories.length > 0) {
            setSelectedRate(editData.RateCodeID || "");
            setSelectedMealPlan(editData.MealPlanID || "");
            setEffectiveFrom(editData.EffectiveFrom ? editData.EffectiveFrom.split("T")[0] : "");
            setEffectiveTo(editData.EffectiveTo ? editData.EffectiveTo.split("T")[0] : "");
            setIsActive(editData.IsActive === 1);

            setRoomEditIds((prev) => ({
                ...prev,
                [editData.RoomTypeID]: editData.RateCodeMealPlanID,
            }));

            setRates((prev) => ({
                ...prev,
                [editData.RoomTypeID]: {
                    base: editData.BaseRate ?? "",
                    double: editData.DoubleRate ?? "",
                    triple: editData.TripleRate ?? "",
                    extraBed: editData.ExtraBed ?? "",
                    extraAdult: editData.ExtraAdultRate ?? "",
                    extraChild: editData.ExtraChildRate ?? "",
                },
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, roomCategories]);

    const handleRateChange = (roomId, fieldKey, value) => {
        setRates((prev) => ({
            ...prev,
            [roomId]: {
                ...prev[roomId],
                [fieldKey]: value,
            },
        }));
    };

    const toggleCopyAllPopup = (roomId) => {
        setCopyAllPopup((prev) => {
            const isClosing = prev.open && prev.roomId === roomId;

            if (!isClosing) {
                setAdjustmentType("Add Fixed Amount ($)");
                setAdjustmentValue("");
                setSelectedApplyFields([]);
            }

            return isClosing
                ? { open: false, roomId: null }
                : { open: true, roomId };
        });
    };

    const toggleApplyField = (fieldKey) => {
        setSelectedApplyFields((prev) =>
            prev.includes(fieldKey)
                ? prev.filter((key) => key !== fieldKey)
                : [...prev, fieldKey]
        );
    };

    const handleAdjustmentValueChange = (value) => {
        if (adjustmentType === "Add Percentage (%)") {
            // Allow values such as 2, 2%, 10, 10.5%
            const cleanedValue = value
                .replace(/[^0-9.%]/g, "")
                .replace(/(?!^)-/g, "");

            const withoutPercent = cleanedValue.replace(/%/g, "");
            const parts = withoutPercent.split(".");
            const validNumber =
                parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : parts[0];

            setAdjustmentValue(
                validNumber === "" ? "" : `${validNumber}%`
            );
            return;
        }

        // Fixed amount accepts only a positive number/decimal.
        const cleanedValue = value.replace(/[^0-9.]/g, "");
        const parts = cleanedValue.split(".");
        const validNumber =
            parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : parts[0];

        setAdjustmentValue(validNumber);
    };

    const handleAdjustmentTypeChange = (value) => {
        setAdjustmentType(value);
        setAdjustmentValue("");
    };

    const formatRateValue = (value) => {
        return Number(Number(value).toFixed(2)).toString();
    };

    const handleApplyCopyToAll = () => {
        const normalizedAdjustmentValue = adjustmentValue
            .replace(/%/g, "")
            .trim();

        const adjustment = Number(normalizedAdjustmentValue);

        if (
            normalizedAdjustmentValue === "" ||
            !Number.isFinite(adjustment)
        ) {
            swal("Please enter a valid adjustment value.", {
                icon: "warning",
            });
            return;
        }

        if (adjustment < 0) {
            swal("Adjustment value cannot be negative.", {
                icon: "warning",
            });
            return;
        }

        if (selectedApplyFields.length === 0) {
            swal("Please select at least one rate field.", {
                icon: "warning",
            });
            return;
        }

        const sourceRoomIndex = roomCategories.findIndex(
            (room) => room.id === copyAllPopup.roomId
        );

        if (sourceRoomIndex === -1) {
            swal("Source room was not found.", {
                icon: "error",
            });
            return;
        }

        const sourceRoomId = roomCategories[sourceRoomIndex].id;
        const sourceRoomRates = rates[sourceRoomId] || {};

        const emptySelectedFields = selectedApplyFields.filter((fieldKey) => {
            const value = sourceRoomRates[fieldKey];
            return value === "" || value === null || value === undefined || !Number.isFinite(Number(value));
        });

        if (emptySelectedFields.length > 0) {
            const fieldNames = emptySelectedFields
                .map((fieldKey) => rateFields.find((field) => field.key === fieldKey)?.label)
                .filter(Boolean)
                .join(", ");

            swal(`Please enter the first room value for: ${fieldNames}`, {
                icon: "warning",
            });
            return;
        }

        setRates((prev) => {
            const updatedRates = { ...prev };

            roomCategories.forEach((room) => {
                updatedRates[room.id] = {
                    ...(prev[room.id] || emptyRateSet()),
                };
            });

            selectedApplyFields.forEach((fieldKey) => {
                let runningValue = Number(sourceRoomRates[fieldKey]);

                for (let index = sourceRoomIndex + 1; index < roomCategories.length; index += 1) {
                    if (adjustmentType === "Add Fixed Amount ($)") {
                        runningValue += adjustment;
                    } else {
                        runningValue += (runningValue * adjustment) / 100;
                    }

                    const targetRoomId = roomCategories[index].id;
                    updatedRates[targetRoomId][fieldKey] = formatRateValue(runningValue);
                }
            });

            return updatedRates;
        });

        setCopyAllPopup({ open: false, roomId: null });
        setAdjustmentValue("");
        setSelectedApplyFields([]);

        swal("Copy To All Successful", {
            icon: "success",
            timer: 2000,
            buttons: false,
        });
    };

    // ── Single submit handler: saves every filled room, create or update ──
    const handleSubmit = async () => {
        if (!selectedMealPlan) {
            swal("Error", "Please select Meal Plan", "error");
            return;
        }

        const filledRooms = roomCategories.filter((room) => {
            const roomRate = rates[room.id];
            return roomRate && Object.values(roomRate).some((v) => v !== "" && v !== undefined);
        });

        if (filledRooms.length === 0) {
            swal("Error", "Please fill rates for at least one room", "error");
            return;
        }

        try {
            setLoading(true);

            const promises = filledRooms.map((room) => {
                const roomRate = rates[room.id] || {};
                const editId = roomEditIds[room.id] || 0; // 0 = create, otherwise update

                return PropertySaveMapping(
                    editId,
                    selectedRate || 0,
                    selectedMealPlan,
                    room.id,
                    roomRate.base || 0,
                    roomRate.double || 0,
                    roomRate.triple || 0,
                    roomRate.extraBed || 0,
                    roomRate.extraAdult || 0,
                    roomRate.extraChild || 0,
                    effectiveFrom,
                    effectiveTo,
                    isActive ? 1 : 0
                );
            });

            await Promise.all(promises);

            swal("Data Saved Successfully", {
                icon: "success",
                buttons: { confirm: { text: "OK", className: "btn btn-success" } },
            }).then(() => {
                navigate("/Property/RateInventory");
            });
        } catch (error) {
            console.error("handleSubmit Error:", error);
            swal("Failed to save data!", { icon: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wrapper property-details-page">
            <SideBar />
            <div className="main-panel">
                <Hearder />
                <div className="container">
                    <div className="page-inner">

                        {loading && (
                            <FullScreenLoader />
                        )}

                        <div >
                            <div className="bri-card bri-header-card mb-3">
                                <div className="bri-header-row">
                                    <div>
                                        <h2 className="bri-title">Add Bulk Rate </h2>
                                        <p className="bri-subtitle">
                                            Synchronize pricing data between direct and third-party channels.
                                        </p>
                                    </div>

                                </div>

                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="mb-3">
                                            <label className="form-label">Rate Code</label>
                                            <select className="form-control form-select"
                                                value={selectedRate}
                                                onChange={(e) => setSelectedRate(e.target.value)}>
                                                <option value="">Select code</option>
                                                {ratecode.map((item) => (
                                                    <option key={item.RateCodeID} value={item.RateCodeID}>
                                                        {item.Name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <div className="mb-3">
                                            <label className="form-label">Meal Plan</label>
                                            <select className="form-control form-select"
                                                value={selectedMealPlan}
                                                onChange={(e) => setSelectedMealPlan(e.target.value)}>
                                                <option value="">Select Plan</option>
                                                {mealplan.map((item) => (
                                                    <option key={item.MealPlanID} value={item.MealPlanID}>
                                                        {item.Name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-sm-2">
                                        <div className="mb-3">
                                            <label className="form-label">Effective From</label>
                                            <input type="date" className="form-control"
                                                value={effectiveFrom}
                                                onChange={(e) => setEffectiveFrom(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-2">
                                        <div className="mb-3">
                                            <label className="form-label">Effective To</label>
                                            <input type="date" className="form-control"
                                                value={effectiveTo}
                                                onChange={(e) => setEffectiveTo(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-2">
                                        <div className="mt-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    style={{ width: "4rem", height: "2rem" }}
                                                    className="form-check-input custom-switch"
                                                    type="checkbox"
                                                    id="activeSwitch"
                                                    checked={isActive}
                                                    onChange={(e) => setIsActive(e.target.checked)}
                                                />
                                            </div>
                                            <label className="form-label d-block ">Active</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {roomCategories.map((room, index) => (
                                <div className="bri-card bri-room-card mb-3" key={room.id}>
                                    <div className="bri-room-header">
                                        <div className="bri-room-title-wrap">
                                            <span className="bri-room-bar" />
                                            <h3 className="bri-room-title">{room.title}</h3>
                                        </div>
                                        <div className="bri-room-actions">
                                            {index === 0 && (
                                                <div className="bri-copy-all-wrap">
                                                    <button
                                                        type="button"
                                                        className="bri-copy-all"
                                                        onClick={() => toggleCopyAllPopup(room.id)}
                                                    >
                                                        <Icon name="content_copy" className="bri-copy-icon" />
                                                        Copy to All
                                                    </button>

                                                    {copyAllPopup.open && copyAllPopup.roomId === room.id && (
                                                        <div className="bri-adjustment-popup" ref={popupRef}>
                                                            <label className="bri-adjustment-label">
                                                                Adjustment Type
                                                            </label>

                                                            <div className="bri-select bri-adjustment-select">
                                                                <select
                                                                    value={adjustmentType}
                                                                    onChange={(e) =>
                                                                        handleAdjustmentTypeChange(e.target.value)
                                                                    }
                                                                >
                                                                    <option>Add Fixed Amount ($)</option>
                                                                    <option>Add Percentage (%)</option>
                                                                </select>

                                                                <Icon
                                                                    name="expand_more"
                                                                    className="bri-select-chevron"
                                                                />
                                                            </div>

                                                            <label className="bri-adjustment-label">
                                                                Value
                                                            </label>

                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                className="bri-adjustment-value"
                                                                placeholder={
                                                                    adjustmentType === "Add Percentage (%)"
                                                                        ? "e.g. 2% or 10%"
                                                                        : "e.g. 100"
                                                                }
                                                                value={adjustmentValue}
                                                                onChange={(e) =>
                                                                    handleAdjustmentValueChange(e.target.value)
                                                                }
                                                            />

                                                            <label className="bri-adjustment-label mt-3">
                                                                Apply On
                                                            </label>

                                                            <div className="row mt-2">
                                                                {rateFields.map((field) => {
                                                                    const checkboxId = `apply-${field.key}`;

                                                                    return (
                                                                        <div className="col-4 mb-2" key={field.key}>
                                                                            <div className="form-check">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    id={checkboxId}
                                                                                    checked={selectedApplyFields.includes(field.key)}
                                                                                    onChange={() => toggleApplyField(field.key)}
                                                                                />
                                                                                <label
                                                                                    className="form-check-label"
                                                                                    htmlFor={checkboxId}
                                                                                >
                                                                                    {field.label}
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>

                                                            <div className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={handleApplyCopyToAll}
                                                                >
                                                                    Apply
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bri-rate-grid">
                                        {rateFields.map((field) => (
                                            <div className="bri-rate-field" key={field.key}>
                                                <span className="bri-rate-label">{field.label}</span>
                                                <div className="d-flex gap-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="bri-rate-input"
                                                        placeholder="Entry"
                                                        value={rates[room.id]?.[field.key] || ""}
                                                        onChange={(e) =>
                                                            handleRateChange(room.id, field.key, e.target.value)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <div className="bri-footer-action">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm me-2"
                                    onClick={() => navigate("/Property/RateInventory")}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmit}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}